/**
 * `GbGrecaptchaComponent` integrates Google reCAPTCHA into your Angular application.
 * It supports both visible and invisible reCAPTCHA modes and emits the response token upon successful validation.
 *
 * Inputs:
 * - `recaptchaSiteKey`: The Google reCAPTCHA site key.
 * - `recaptchaSize`: The size of the reCAPTCHA widget ('normal' or 'invisible'), normal is the checkbox and invisible is the badge.
 *
 * Outputs:
 * - `recaptchaSuccess`: Emits the reCAPTCHA response token after successful validation.
 *
 * Usage: place above the submit button in a form because the 'normal' type will render a checkbox.
 */
import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import * as Sentry from '@sentry/angular'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'

declare const grecaptcha: any // Declare grecaptcha to avoid TS erro
declare global {
  interface Window {
    grecaptcha: any
  }
}

@Component({
  selector: 'gb-grecaptcha',
  imports: [],
  templateUrl: './gb-grecaptcha.component.html',
  styleUrl: './gb-grecaptcha.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class GbGrecaptchaComponent implements OnInit {
  @Input() recaptchaSize: 'normal' | 'invisible' = this.appConfig.recaptchaSize
  @Output() recaptchaSuccess: EventEmitter<string> = new EventEmitter<string>()
  @Output() recaptchaCancel: EventEmitter<void> = new EventEmitter<void>()
  @Output() recaptchaExecute: EventEmitter<void> = new EventEmitter<void>()
  @ViewChild('recaptcha') recapctchaElem!: ElementRef

  constructor(@Inject(APP_CONFIG) protected appConfig: any) {}

  ngOnInit(): void {
    if (typeof window.grecaptcha === 'undefined') {
      const script = document.createElement('script')
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
      script.async = true
      document.body.appendChild(script)
      script.onload = () => {
        this.initRecaptcha() // Call initialization function after the script is loaded
      }
    } else {
      this.initRecaptcha()
    }
  }

  initRecaptcha(): void {
    if (typeof window.grecaptcha !== 'undefined') {
      window.grecaptcha.ready(() => {
        // Now it's safe to call grecaptcha.render
        this.renderRecaptcha()
      })
    } else {
      const errorMessage = 'Google reCAPTCHA is not loaded.'
      console.error(errorMessage)
      Sentry.captureMessage(errorMessage)
    }
  }

  renderRecaptcha(): void {
    if (typeof grecaptcha !== 'undefined') {
      grecaptcha.render(this.recapctchaElem.nativeElement, {
        sitekey: this.appConfig.recaptchaSiteKey,
        size: this.recaptchaSize,
        callback: (response: string) => this.onRecaptchaSuccess(response),
      })
    }
  }

  execute(): void {
    if (this.recaptchaSize === 'invisible') {
      grecaptcha.execute()
      this.recaptchaExecute.emit()
      this.observeRecaptchaIframe()
    }
  }

  private observeRecaptchaIframe(): void {
    const bodyObserver = new MutationObserver(() => {
      const iframe = document.querySelector('iframe[src^="https://www.google.com/recaptcha"][src*="bframe"]')
      if (iframe) {
        // once iframe is found, observe its container for close/cancel
        this.observeRecaptchaCancel(iframe)
        bodyObserver.disconnect()
      }
    })
    bodyObserver.observe(document.body, { childList: true, subtree: true })
  }

  private observeRecaptchaCancel(iframe: Element): void {
    const container = iframe.parentNode?.parentNode as HTMLElement
    if (!container) return

    const observer = new MutationObserver(() => {
      if (container.style.visibility === 'hidden' && !grecaptcha.getResponse()) {
        this.recaptchaCancel.emit()
        observer.disconnect()
      }
    })

    observer.observe(container, { attributes: true, attributeFilter: ['style'] })
  }

  reset(): void {
    if (typeof grecaptcha !== 'undefined') grecaptcha.reset()
  }

  onRecaptchaSuccess(captchaResponse: string): void {
    this.recaptchaSuccess.emit(captchaResponse)
  }
}
